/**
 * shared/schema/mobile.ts — Task #2118, Task #2223.
 *
 * Mobile Backend Core Route Surface schema. Five tables backing the
 * nw-mobile-scan-wallet app:
 *
 *   - `mobile_scan_events`              — idempotency ledger for every scan evaluated
 *                                         via POST /api/mobile/scans/evaluate.
 *   - `mobile_devices`                  — registered mobile devices per tenant.
 *   - `mobile_shifts`                   — operator shift lifecycle records.
 *   - `mobile_replay_batches`           — offline replay batch results.
 *   - `mobile_notification_preferences` — per-user, per-category push opt-in flags.
 *
 * NEW shared file — additive only. No existing shared files were edited.
 * Justified precedent: Task #1960 (public-surface-hardening.ts) and
 * Task #1964 (qr-subsystem.ts).
 */
import { sql } from "drizzle-orm";
import {
  pgTable,
  text,
  varchar,
  boolean,
  integer,
  timestamp,
  jsonb,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const mobileScanEvents = pgTable(
  "mobile_scan_events",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id").notNull(),
    requestId: text("request_id").notNull(),
    deviceId: text("device_id").notNull(),
    capturedAt: text("captured_at").notNull(),
    mode: text("mode").notNull(),
    workerId: text("worker_id"),
    permitId: text("permit_id"),
    gateId: text("gate_id"),
    outcome: text("outcome").notNull(),
    accessEventId: text("access_event_id"),
    attendanceId: text("attendance_id"),
    source: text("source").notNull().default("live"),
    denialReasons: text("denial_reasons").array().default([]),
    context: jsonb("context")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    idxScanTenant: index("idx_mobile_scan_events_tenant").on(t.tenantId),
    idxScanDevice: index("idx_mobile_scan_events_device").on(t.deviceId, t.capturedAt),
    idxScanWorker: index("idx_mobile_scan_events_worker").on(t.workerId, t.capturedAt),
    idxScanIdempotency: uniqueIndex("uq_mobile_scan_events_idempotency").on(
      t.requestId,
      t.deviceId,
    ),
  }),
);
export const insertMobileScanEventSchema = createInsertSchema(mobileScanEvents).omit({
  id: true,
  createdAt: true,
});
export type InsertMobileScanEvent = z.infer<typeof insertMobileScanEventSchema>;
export type MobileScanEvent = typeof mobileScanEvents.$inferSelect;

export const mobileDevices = pgTable(
  "mobile_devices",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id").notNull(),
    deviceId: text("device_id").notNull(),
    platform: text("platform").notNull().default("unknown"),
    appVersion: text("app_version"),
    userId: text("user_id"),
    pushToken: text("push_token"),
    signingSecretHash: text("signing_secret_hash"),
    registeredAt: timestamp("registered_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    lastHeartbeatAt: timestamp("last_heartbeat_at", { withTimezone: true }),
    metadata: jsonb("metadata")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
  },
  (t) => ({
    uqTenantDevice: uniqueIndex("uq_mobile_devices_tenant_device").on(
      t.tenantId,
      t.deviceId,
    ),
    idxDeviceUser: index("idx_mobile_devices_user").on(t.tenantId, t.userId),
  }),
);
export const insertMobileDeviceSchema = createInsertSchema(mobileDevices).omit({
  id: true,
  registeredAt: true,
});
export type InsertMobileDevice = z.infer<typeof insertMobileDeviceSchema>;
export type MobileDevice = typeof mobileDevices.$inferSelect;

export const mobileShifts = pgTable(
  "mobile_shifts",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id").notNull(),
    userId: text("user_id").notNull(),
    gateId: text("gate_id"),
    deviceId: text("device_id"),
    startedAt: timestamp("started_at", { withTimezone: true }).notNull().defaultNow(),
    endedAt: timestamp("ended_at", { withTimezone: true }),
    status: text("status").notNull().default("active"),
    summary: jsonb("summary")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    notes: text("notes"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    idxShiftUser: index("idx_mobile_shifts_user").on(t.tenantId, t.userId, t.status),
    idxShiftGate: index("idx_mobile_shifts_gate").on(t.tenantId, t.gateId, t.status),
  }),
);
export const insertMobileShiftSchema = createInsertSchema(mobileShifts).omit({
  id: true,
  createdAt: true,
});
export type InsertMobileShift = z.infer<typeof insertMobileShiftSchema>;
export type MobileShift = typeof mobileShifts.$inferSelect;

export const mobileReplayBatches = pgTable(
  "mobile_replay_batches",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id").notNull(),
    deviceId: text("device_id").notNull(),
    submittedAt: timestamp("submitted_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    itemCount: integer("item_count").notNull().default(0),
    replayed: integer("replayed").notNull().default(0),
    failed: integer("failed").notNull().default(0),
    duplicates: integer("duplicates").notNull().default(0),
    items: jsonb("items")
      .$type<unknown[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
  },
  (t) => ({
    idxBatchDevice: index("idx_mobile_replay_batches_device").on(t.tenantId, t.deviceId),
    idxBatchAt: index("idx_mobile_replay_batches_at").on(t.tenantId, t.submittedAt),
  }),
);
export const insertMobileReplayBatchSchema = createInsertSchema(mobileReplayBatches).omit({
  id: true,
  submittedAt: true,
});
export type InsertMobileReplayBatch = z.infer<typeof insertMobileReplayBatchSchema>;
export type MobileReplayBatch = typeof mobileReplayBatches.$inferSelect;

// ─── Push Notification Preferences ───────────────────────────────────────────

/**
 * The set of push notification categories workers can individually opt in/out
 * of. All categories default to enabled (true). Stored per (tenant, user) pair
 * so preferences survive device re-registrations.
 *
 * Categories:
 *   permit_reminders — PTW permit status updates and reminders
 *   muster_alerts    — Emergency muster notifications
 *   access_alerts    — Access control decisions and alerts
 *   shift_reminders  — Shift start / end reminders
 */
export const PUSH_NOTIFICATION_CATEGORIES = [
  "permit_reminders",
  "muster_alerts",
  "access_alerts",
  "shift_reminders",
] as const;

export type PushNotificationCategory = (typeof PUSH_NOTIFICATION_CATEGORIES)[number];

export const mobileNotificationPreferences = pgTable(
  "mobile_notification_preferences",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id").notNull(),
    userId: text("user_id").notNull(),
    permitReminders: boolean("permit_reminders").notNull().default(true),
    musterAlerts: boolean("muster_alerts").notNull().default(true),
    accessAlerts: boolean("access_alerts").notNull().default(true),
    shiftReminders: boolean("shift_reminders").notNull().default(true),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    uqTenantUser: uniqueIndex("uq_mobile_notification_prefs_tenant_user").on(
      t.tenantId,
      t.userId,
    ),
    idxPrefsTenant: index("idx_mobile_notification_prefs_tenant").on(t.tenantId),
  }),
);
export const insertMobileNotificationPreferencesSchema = createInsertSchema(
  mobileNotificationPreferences,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertMobileNotificationPreferences = z.infer<
  typeof insertMobileNotificationPreferencesSchema
>;
export type MobileNotificationPreferences =
  typeof mobileNotificationPreferences.$inferSelect;

// ─── Mobile Issue Reports ─────────────────────────────────────────────────────

/**
 * Workers can flag access issues (wrong decision, missing worker, etc.) from
 * the mobile app. Issues are linked to an access or receipt event and are
 * triaged by tenant admins.
 *
 * Categories:
 *   WRONG_DECISION    — the access engine returned an unexpected allow/deny
 *   MISSING_WORKER    — worker not found / not recognised
 *   WRONG_PERSON      — QR presented by an impersonator
 *   TECHNICAL_ERROR   — app crash, scan failure, connectivity issue
 *   SUPPORT           — general support request
 *   OTHER             — uncategorised
 */
export const MOBILE_ISSUE_CATEGORIES = [
  "WRONG_DECISION",
  "MISSING_WORKER",
  "WRONG_PERSON",
  "TECHNICAL_ERROR",
  "SUPPORT",
  "OTHER",
] as const;

export type MobileIssueCategory = (typeof MOBILE_ISSUE_CATEGORIES)[number];

export const MOBILE_ISSUE_STATUSES = [
  "open",
  "in_progress",
  "resolved",
  "closed",
] as const;

export type MobileIssueStatus = (typeof MOBILE_ISSUE_STATUSES)[number];


export const mobileIssueReports = pgTable(
  "mobile_issue_reports",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id").notNull(),
    eventId: text("event_id").notNull(),
    reportedByUserId: text("reported_by_user_id").notNull(),
    issueCategory: text("issue_category").notNull(),
    issueDescription: text("issue_description"),
    reporterContact: text("reporter_contact"),
    status: text("status").notNull().default("open"),
    resolvedAt: timestamp("resolved_at", { withTimezone: true }),
    resolvedByUserId: text("resolved_by_user_id"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    idxIssueTenant: index("idx_mobile_issue_reports_tenant").on(t.tenantId),
    idxIssueEvent: index("idx_mobile_issue_reports_event").on(t.eventId),
    idxIssueStatus: index("idx_mobile_issue_reports_status").on(t.tenantId, t.status),
  }),
);

export const insertMobileIssueReportSchema = createInsertSchema(mobileIssueReports)
  .omit({ id: true, createdAt: true })
  .extend({
    issueDescription: z.string().max(2000).optional(),
  });
export type InsertMobileIssueReport = z.infer<typeof insertMobileIssueReportSchema>;
export type MobileIssueReport = typeof mobileIssueReports.$inferSelect;

// ── Task #3461 — Mobile Acknowledgements (PTW mobile safety fix) ──────────
//
// Persists every worker briefing acknowledgement submitted via the mobile API
// (`POST /api/v1/ptw/permits/:id/acknowledgements`) so that acknowledgements
// are durable and auditable rather than fire-and-forget.
export const mobileAcknowledgements = pgTable(
  "mobile_acknowledgements",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id").notNull(),
    permitId: text("permit_id").notNull(),
    acknowledgedBy: text("acknowledged_by").notNull(),
    ackType: text("ack_type").notNull().default("worker_briefing_ack"),
    signature: text("signature").notNull(),
    notes: text("notes"),
    source: text("source"),
    acknowledgedAt: timestamp("acknowledged_at", { withTimezone: true }).notNull().defaultNow(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    idxAckTenantPermit: index("idx_mobile_acknowledgements_tenant_permit").on(t.tenantId, t.permitId),
    idxAckTenantUser: index("idx_mobile_acknowledgements_tenant_user").on(t.tenantId, t.acknowledgedBy),
  }),
);
export const insertMobileAcknowledgementSchema = createInsertSchema(mobileAcknowledgements)
  .omit({ id: true, tenantId: true, createdAt: true });
export type InsertMobileAcknowledgement = z.infer<typeof insertMobileAcknowledgementSchema>;
export type MobileAcknowledgement = typeof mobileAcknowledgements.$inferSelect;
